import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useMemberPortal } from './MemberPortal';
import { getMyMemberships } from '../../lib/memberDb';
import { differenceInDays, parseISO, format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { Dumbbell } from 'lucide-react';

export default function MyQR() {
  const { memberRecord, gym } = useMemberPortal();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyMemberships(memberRecord.id).then(data => {
      const active = data.find(m => m.status === 'active' || m.status === 'frozen');
      setMembership(active || null);
      setLoading(false);
    });
  }, [memberRecord.id]);

  const qrValue = `gym-member:${memberRecord.id}`;
  const daysLeft = membership?.endDate
    ? differenceInDays(parseISO(membership.endDate), new Date())
    : null;

  return (
    <div className="p-4 flex flex-col items-center">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-5 self-start">디지털 회원증</h2>

      {/* 카드 */}
      <div className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white">

        {/* 헬스장 헤더 */}
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-white truncate">{gym?.name || 'GYM Manager'}</p>
            <p className="text-xs text-white/60">회원증 · Member Card</p>
          </div>
        </div>

        <div className="mx-6 border-t border-white/20" />

        {/* 회원 정보 */}
        <div className="px-6 py-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 border-2 border-white/30">
            <span className="text-2xl font-bold">{memberRecord.name?.[0] || '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-xl font-bold leading-tight">{memberRecord.name}</p>
            {memberRecord.phone && (
              <p className="text-xs text-white/60 mt-0.5">{memberRecord.phone}</p>
            )}
            {memberRecord.createdAt && (
              <p className="text-xs text-white/40 mt-0.5">
                등록 {format(parseISO(memberRecord.createdAt), 'yyyy.MM.dd', { locale: ko })}
              </p>
            )}
          </div>
        </div>

        {/* 회원권 상태 */}
        {!loading && (
          <div className="mx-6 mb-5">
            {membership ? (
              <div className="bg-white/10 backdrop-blur rounded-2xl px-4 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs text-white/60 mb-0.5">현재 회원권</p>
                  <p className="font-semibold truncate">{membership.productName}</p>
                  {membership.endDate && (
                    <p className="text-xs text-white/50 mt-0.5">
                      ~ {format(parseISO(membership.endDate), 'yyyy.MM.dd')}
                    </p>
                  )}
                </div>
                {daysLeft !== null && (
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-xs text-white/60">잔여</p>
                    <p className={`text-2xl font-black tabular-nums ${daysLeft <= 7 ? 'text-red-300' : 'text-white'}`}>
                      {Math.max(0, daysLeft)}
                    </p>
                    <p className="text-xs text-white/60">일</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                <p className="text-sm text-white/60">이용중인 회원권이 없습니다</p>
              </div>
            )}
          </div>
        )}

        {/* QR 코드 */}
        <div className="bg-white mx-6 mb-6 rounded-2xl p-5 flex flex-col items-center gap-3">
          <QRCodeSVG
            value={qrValue}
            size={180}
            level="M"
            includeMargin={false}
          />
          <p className="text-xs text-gray-400 text-center">
            출석 체크 시 스캐너에 보여주세요
          </p>
        </div>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
        QR코드는 본인만 사용하세요. 타인에게 공유하지 마세요.
      </p>
    </div>
  );
}
